import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatDuration } from '../../lib/utils';
import { Headphones, MapPin, Clock, Ticket, Heart, Activity, Play, Download, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function GuideCard({ guide, isFav, onToggleFav, index }) {
    return (
        <Card
            className="bg-[#121620] border-white/10 overflow-hidden group card-hover animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
        >
            <Link to={`/guide/${guide.id}`}>
                <div className="relative aspect-[16/10]">
                    {guide.featured_image ? (
                        <img
                            src={guide.featured_image}
                            alt={guide.title}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center">
                            <Headphones className="w-10 h-10 sm:w-12 sm:h-12 text-gold/50" />
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-transparent to-transparent" />
                </div>
            </Link>
            <div className="absolute top-3 right-3 z-10">
                <button
                    onClick={(e) => { e.preventDefault(); onToggleFav(guide.id); }}
                    className={`w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-black/50 backdrop-blur flex items-center justify-center transition-colors ${
                        isFav ? 'text-red-400' : 'text-white/70 hover:text-gold'
                    }`}
                    data-testid={`fav-btn-${guide.id}`}
                >
                    <Heart className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} />
                </button>
            </div>
            <Link to={`/guide/${guide.id}`}>
                <CardContent className="p-3 sm:p-4">
                    <div className="flex items-center gap-2 text-gold text-xs mb-1.5 sm:mb-2">
                        <MapPin className="w-3 h-3" />
                        <span>{guide.city_name}</span>
                    </div>
                    <h3 className="font-outfit font-semibold text-white text-sm sm:text-base mb-1.5 sm:mb-2 group-hover:text-gold transition-colors line-clamp-2">
                        {guide.title}
                    </h3>
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] sm:text-xs text-muted-foreground px-2 py-0.5 sm:py-1 rounded-full bg-white/5">
                            {guide.category}
                        </span>
                        <div className="flex items-center gap-1 text-muted-foreground text-xs sm:text-sm">
                            <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            <span>{formatDuration(guide.audio_duration)}</span>
                        </div>
                    </div>
                </CardContent>
            </Link>
        </Card>
    );
}

const ACTIVITY_ICONS = {
    listen: Play,
    download: Download,
    redeem: KeyRound,
};

const ACTIVITY_LABELS = {
    listen: 'Listened to',
    download: 'Downloaded offline',
    redeem: 'Redeemed code for',
};

function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
}

export default function TravelerDashboard() {
    const [guides, setGuides] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [favIds, setFavIds] = useState(new Set());
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('guides');

    useEffect(() => {
        Promise.all([fetchGuides(), fetchFavorites(), fetchActivity()]).finally(() => setLoading(false));
    }, []);

    const fetchGuides = async () => {
        try {
            const res = await axios.get(`${API}/api/traveler/my-guides`, { withCredentials: true });
            setGuides(res.data);
        } catch (e) {
            console.error('Failed to fetch guides:', e);
        }
    };

    const fetchFavorites = async () => {
        try {
            const res = await axios.get(`${API}/api/traveler/favorites`, { withCredentials: true });
            setFavorites(res.data);
            setFavIds(new Set(res.data.map(a => a.id)));
        } catch (e) {
            console.error('Failed to fetch favorites:', e);
        }
    };

    const fetchActivity = async () => {
        try {
            const res = await axios.get(`${API}/api/traveler/activity?limit=30`, { withCredentials: true });
            setActivities(res.data);
        } catch (e) {
            console.error('Failed to fetch activity:', e);
        }
    };

    const toggleFavorite = async (attractionId) => {
        try {
            if (favIds.has(attractionId)) {
                await axios.delete(`${API}/api/traveler/favorites/${attractionId}`, { withCredentials: true });
                setFavIds(prev => { const n = new Set(prev); n.delete(attractionId); return n; });
                setFavorites(prev => prev.filter(f => f.id !== attractionId));
                toast.success('Removed from favorites');
            } else {
                await axios.post(`${API}/api/traveler/favorites/${attractionId}`, {}, { withCredentials: true });
                setFavIds(prev => new Set(prev).add(attractionId));
                fetchFavorites();
                toast.success('Added to favorites');
            }
        } catch (e) {
            toast.error('Failed to update favorites');
        }
    };

    if (loading) {
        return (
            <DashboardLayout type="traveler">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="traveler">
            <div className="space-y-5 sm:space-y-8" data-testid="traveler-dashboard">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                    <div>
                        <h1 className="font-outfit text-xl sm:text-2xl lg:text-3xl font-bold text-white">My Audio Guides</h1>
                        <p className="text-muted-foreground text-sm mt-1">Your purchased and redeemed guides</p>
                    </div>
                    <Link to="/redeem" className="shrink-0">
                        <Button className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2 w-full sm:w-auto h-11 sm:h-10" data-testid="redeem-code-btn">
                            <Ticket className="w-4 h-4" /> Redeem Code
                        </Button>
                    </Link>
                </div>

                {/* Tabs */}
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-[#121620] border border-white/10 w-full sm:w-auto grid grid-cols-3 sm:inline-flex h-11 sm:h-10">
                        <TabsTrigger value="guides" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-1.5 sm:gap-2 text-xs sm:text-sm">
                            <Headphones className="w-4 h-4" /> Guides
                        </TabsTrigger>
                        <TabsTrigger value="favorites" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-1.5 sm:gap-2 text-xs sm:text-sm">
                            <Heart className="w-4 h-4" /> Favorites
                        </TabsTrigger>
                        <TabsTrigger value="activity" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-1.5 sm:gap-2 text-xs sm:text-sm" data-testid="activity-tab">
                            <Activity className="w-4 h-4" /> Activity
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="guides" className="mt-4 sm:mt-6">
                        {guides.length > 0 ? (
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                                {guides.map((guide, i) => (
                                    <GuideCard
                                        key={guide.id}
                                        guide={guide}
                                        isFav={favIds.has(guide.id)}
                                        onToggleFav={toggleFavorite}
                                        index={i}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Card className="bg-[#121620] border-white/10">
                                <CardContent className="p-8 sm:p-12 text-center">
                                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                                        <Headphones className="w-7 h-7 sm:w-8 sm:h-8 text-gold" />
                                    </div>
                                    <h3 className="font-outfit font-semibold text-white text-lg sm:text-xl mb-2">No guides yet</h3>
                                    <p className="text-muted-foreground text-sm mb-6">Redeem a code to start exploring audio guides</p>
                                    <Link to="/redeem">
                                        <Button className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2">
                                            <Ticket className="w-4 h-4" /> Redeem Code
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    <TabsContent value="favorites" className="mt-4 sm:mt-6">
                        {favorites.length > 0 ? (
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                                {favorites.map((guide, i) => (
                                    <GuideCard
                                        key={guide.id}
                                        guide={guide}
                                        isFav={true}
                                        onToggleFav={toggleFavorite}
                                        index={i}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Card className="bg-[#121620] border-white/10">
                                <CardContent className="p-8 sm:p-12 text-center">
                                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                                        <Heart className="w-7 h-7 sm:w-8 sm:h-8 text-gold" />
                                    </div>
                                    <h3 className="font-outfit font-semibold text-white text-lg sm:text-xl mb-2">No favorites yet</h3>
                                    <p className="text-muted-foreground text-sm mb-6">Tap the heart icon on any guide to save it here</p>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Activity Feed Tab */}
                    <TabsContent value="activity" className="mt-4 sm:mt-6" data-testid="activity-feed">
                        {activities.length > 0 ? (
                            <div className="space-y-2 sm:space-y-3">
                                {activities.map((activity) => {
                                    const Icon = ACTIVITY_ICONS[activity.type] || Play;
                                    const label = ACTIVITY_LABELS[activity.type] || 'Interacted with';
                                    return (
                                        <div
                                            key={activity.id}
                                            className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-[#121620] border border-white/10 animate-fade-in"
                                            data-testid={`activity-item-${activity.id}`}
                                        >
                                            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full shrink-0 flex items-center justify-center ${
                                                activity.type === 'listen' ? 'bg-gold/10 text-gold' :
                                                activity.type === 'download' ? 'bg-emerald-500/10 text-emerald-400' :
                                                'bg-blue-500/10 text-blue-400'
                                            }`}>
                                                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs sm:text-sm text-white">
                                                    <span className="text-muted-foreground">{label}</span>{' '}
                                                    <span className="font-medium">{activity.track_title || activity.attraction_title}</span>
                                                </p>
                                                {activity.attraction_title && activity.track_title && (
                                                    <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate">
                                                        {activity.attraction_title} {activity.language && `\u00B7 ${activity.language.toUpperCase()}`}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="text-[10px] sm:text-xs text-muted-foreground shrink-0">{timeAgo(activity.created_at)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <Card className="bg-[#121620] border-white/10">
                                <CardContent className="p-8 sm:p-12 text-center">
                                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                                        <Activity className="w-7 h-7 sm:w-8 sm:h-8 text-gold" />
                                    </div>
                                    <h3 className="font-outfit font-semibold text-white text-lg sm:text-xl mb-2">No activity yet</h3>
                                    <p className="text-muted-foreground text-sm mb-6">Start listening to guides to see your activity here</p>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
}
